import {Parameter} from 'platform/components';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {FullVehicleResponseBody} from '@dms/api/shared';
import {GetMetadaServiceCaseApiResponse} from '@dms/api/workshop';
import {vehiclesRoutes} from '@dms/routes';
import {useGetVehicleHeaderV2, usePermissions} from '@dms/shared';

import {Nullish, buildArray, parseDate, composePath, useNavigate} from 'shared/web';

export function useServiceCaseVehicleDraftParameters(
  serviceCaseId: string | Nullish,
  serviceCase: GetMetadaServiceCaseApiResponse | Nullish,
  vehicle: FullVehicleResponseBody | Nullish,
  hasComments: boolean,
  additionalNote: string | Nullish
) {
  const navigate = useNavigate();
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();
  const [canReadServiceCase] = usePermissions({permissionKeys: ['serviceCaseRead']});

  const navigateToInsights = () => {
    navigate(composePath(vehiclesRoutes.insights, {params: {id: vehicle?.id ?? ''}}));
  };

  const newVehicleHeader = useGetVehicleHeaderV2({
    serviceCaseNumber: serviceCase?.number,
    publicId: vehicle?.publicId,
    vin: vehicle?.vehicle.vin,
    registrationPlate: vehicle?.vehicle.registrationPlate,
    firstRegistrationOn: vehicle?.vehicle.firstRegistrationOn,
    warrantyDate: vehicle?.vehicle.warrantyDate,
    isVehicleDetail: false,
    hasComments,
    additionalNote,
    onIconClick: navigateToInsights,
  });

  if (canReadServiceCase) {
    return newVehicleHeader;
  }

  return buildArray<Parameter>()
    .when(serviceCaseId, serviceCase?.number)
    .when(serviceCaseId && vehicle?.vin, vehicle?.vin)
    .when(serviceCaseId && vehicle?.vehicle.registrationPlate, vehicle?.vehicle.registrationPlate)
    .when(serviceCaseId && vehicle?.vehicle.firstRegistrationOn, () =>
      formatDateTime('dateShort', parseDate(vehicle?.vehicle.firstRegistrationOn ?? ''))
    )
    .when(serviceCaseId && vehicle?.modelSpecification?.warrantyDate, () =>
      formatDateTime('dateShort', parseDate(vehicle?.modelSpecification?.warrantyDate ?? ''))
    )
    .when(
      serviceCaseId && vehicle?.modelSpecification?.warrantyMileage,
      formatNumber(vehicle?.modelSpecification?.warrantyMileage ?? 0)
    );
}
